import { useState, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import { useVeloraStore } from '../store/useVeloraStore';
import { getDailyChallenge, type DailyChallenge } from '../lib/dailyChallenge';

const VOICE_COLORS: Record<string, string> = {
  Bass: '#60a5fa', Baritone: '#818cf8', Tenor: '#a78bfa',
  Alto: '#f472b6', 'Mezzo-Soprano': '#e879f9', Soprano: '#f9a8d4',
};

const DAILY_TIPS = [
  'Warm up before every session to protect your voice.',
  'Consistent 15-min daily practice beats occasional long sessions.',
  'Stay hydrated — water keeps your vocal cords flexible.',
  'Record yourself regularly to hear your own improvement.',
  'Focus on breath support for better control and range.',
  'Relax your jaw and shoulders before singing.',
  'Hum gently to warm up without straining your voice.',
];

export default function Home() {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    currentStreak, longestStreak, sessionCount,
    allTimeNotesInTune, voiceType, lowestNote, highestNote, score,
  } = useVeloraStore();

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' });
  const tip = DAILY_TIPS[new Date().getDate() % DAILY_TIPS.length];
  const [daily, setDaily] = useState<DailyChallenge>(() => getDailyChallenge());

  useEffect(() => {
    const t = setInterval(() => setDaily(getDailyChallenge()), 60_000);
    return () => clearInterval(t);
  }, []);

  const voiceColor = VOICE_COLORS[voiceType ?? ''] ?? '#7F77DD';

  return (
    <div className="flex flex-col min-h-screen pb-24" style={{ background: 'transparent' }}>

      {/* Header */}
      <div className="safe-top px-5 pt-4 pb-4">
        <div className="flex items-start justify-between fade-up">
          <div>
            <p className="text-xs tracking-widest uppercase font-medium mb-0.5"
              style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>{today}</p>
            <h1 className="text-2xl font-bold leading-tight"
              style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>
              {greeting} 👋
            </h1>
            <p className="text-sm mt-0.5" style={{ color: theme.muted }}>Ready to train your voice?</p>
          </div>
          <button onClick={() => navigate('/settings')} className="glass-btn mt-1 p-2.5" style={{ borderRadius: 14 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="3" stroke={theme.accent} strokeWidth="1.8"/>
              <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
                stroke={theme.accent} strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="px-4 flex flex-col gap-3">

        {/* Hero */}
        <div className="fade-up-1 glass-card-strong rounded-3xl p-5 overflow-hidden relative"
          style={{ background: 'rgba(83,74,183,0.22)', borderColor: 'rgba(127,119,221,0.32)' }}>
          <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(155,148,232,0.22), transparent 70%)', filter: 'blur(20px)' }} />
          <div className="flex items-center gap-1.5 mb-2">
            <span className="w-1.5 h-1.5 rounded-full breathe" style={{ background: 'rgba(155,148,232,0.9)' }} />
            <p className="text-xs tracking-widest uppercase font-semibold"
              style={{ color: 'rgba(238,237,254,0.6)', fontFamily: 'Syne, sans-serif' }}>Daily Training</p>
          </div>
          <h2 className="text-xl font-bold mb-1.5" style={{ color: 'white', fontFamily: 'Syne, sans-serif' }}>
            Start Your Session
          </h2>
          <p className="text-xs leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.6)', maxWidth: '78%' }}>{tip}</p>
          <button onClick={() => navigate('/session')} className="glass-btn px-5 py-2.5 text-sm font-semibold"
            style={{ background: 'rgba(255,255,255,0.13)', borderColor: 'rgba(255,255,255,0.20)', color: 'white', fontFamily: 'Syne, sans-serif' }}>
            Start Now →
          </button>
        </div>

        {/* Stats row */}
        <div className="fade-up-2 grid grid-cols-3 gap-2.5">
          {[
            { label: 'Sessions', value: sessionCount, icon: '🎤' },
            { label: 'Streak', value: `${currentStreak}d`, icon: '🔥', sub: longestStreak > 0 ? `best ${longestStreak}d` : undefined },
            { label: 'In Tune', value: allTimeNotesInTune, icon: '🎵' },
          ].map(s => (
            <div key={s.label} className="glass-card rounded-2xl p-3.5 text-center">
              <div className="text-lg mb-1">{s.icon}</div>
              <div className="text-lg font-bold leading-tight"
                style={{ color: theme.accent, fontFamily: 'JetBrains Mono, monospace' }}>{s.value}</div>
              <div className="text-xs mt-0.5" style={{ color: theme.muted }}>{s.label}</div>
              {s.sub && <div className="text-xs mt-0.5" style={{ color: theme.dim }}>{s.sub}</div>}
            </div>
          ))}
        </div>

        {/* Daily Challenge */}
        <div className="fade-up-3 glass-card rounded-2xl p-4"
          style={daily.completed ? { background: 'rgba(74,222,128,0.07)', borderColor: 'rgba(74,222,128,0.22)' } : {}}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold tracking-widest uppercase"
              style={{ color: daily.completed ? '#4ade80' : theme.primary, fontFamily: 'Syne, sans-serif' }}>
              Daily Challenge
            </span>
            <span className="glass-pill px-2.5 py-0.5 text-xs font-bold"
              style={{ color: daily.completed ? '#4ade80' : theme.accent }}>+{daily.xp} XP</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-2xl">{daily.completed ? '✅' : daily.icon}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: daily.completed ? '#4ade80' : theme.text }}>{daily.title}</p>
              <p className="text-xs mt-0.5" style={{ color: daily.completed ? 'rgba(74,222,128,0.7)' : theme.muted }}>{daily.desc}</p>
            </div>
            {!daily.completed && (
              <button onClick={() => navigate('/session')} className="glass-btn glass-btn-accent px-3 py-1.5 text-xs font-semibold shrink-0"
                style={{ color: '#fff', fontFamily: 'Syne, sans-serif' }}>Go →</button>
            )}
          </div>
        </div>

        {/* Voice type */}
        {voiceType ? (
          <div className="fade-up-4 glass-card rounded-2xl p-4 flex items-center gap-4"
            style={{ background: `${voiceColor}10`, borderColor: `${voiceColor}25` }}>
            <div className="w-[52px] h-[52px] rounded-2xl flex items-center justify-center text-2xl shrink-0"
              style={{ background: `${voiceColor}18`, border: `1px solid ${voiceColor}28` }}>🎭</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs tracking-widest uppercase font-medium"
                style={{ color: voiceColor, fontFamily: 'Syne, sans-serif' }}>Your Voice Type</p>
              <p className="text-xl font-bold" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>{voiceType}</p>
              {lowestNote && highestNote && (
                <p className="text-xs font-mono mt-0.5 truncate" style={{ color: theme.muted }}>{lowestNote} – {highestNote}</p>
              )}
            </div>
            <button onClick={() => navigate('/range')} className="glass-btn px-3 py-1.5 text-xs font-medium shrink-0"
              style={{ color: voiceColor, borderColor: `${voiceColor}30`, background: `${voiceColor}10` }}>Retest</button>
          </div>
        ) : (
          <button onClick={() => navigate('/range')}
            className="fade-up-4 glass-card rounded-2xl p-4 flex items-center gap-3 w-full text-left transition-all active:scale-95"
            style={{ borderStyle: 'dashed', borderColor: 'rgba(83,74,183,0.35)' }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0"
              style={{ background: 'rgba(83,74,183,0.14)' }}>🎤</div>
            <div>
              <p className="text-sm font-semibold" style={{ color: theme.text }}>Discover Your Voice Type</p>
              <p className="text-xs mt-0.5" style={{ color: theme.muted }}>Take the vocal range test</p>
            </div>
            <div className="ml-auto text-lg" style={{ color: theme.primary }}>→</div>
          </button>
        )}

        {/* Quick actions */}
        <div className="fade-up-5 grid grid-cols-2 gap-2.5">
          <button onClick={() => navigate('/songs')} className="glass-card rounded-2xl p-4 text-left transition-all active:scale-95">
            <div className="text-2xl mb-2.5">🎵</div>
            <p className="text-sm font-semibold" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>Sing a Song</p>
            <p className="text-xs mt-0.5" style={{ color: theme.muted }}>Tracks & practice</p>
          </button>
          <button onClick={() => navigate('/achievements')} className="glass-card rounded-2xl p-4 text-left transition-all active:scale-95">
            <div className="text-2xl mb-2.5">🏆</div>
            <p className="text-sm font-semibold" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>Progress</p>
            <p className="text-xs mt-0.5" style={{ color: theme.muted }}>View achievements</p>
          </button>
        </div>

        {/* Last score */}
        {score > 0 && (
          <div className="fade-up-6 glass-card rounded-2xl p-4 flex items-center gap-3">
            <div className="relative shrink-0">
              <svg width="52" height="52" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.8"/>
                <circle cx="18" cy="18" r="15.9" fill="none" stroke={theme.accent} strokeWidth="2.8"
                  strokeDasharray={`${score} 100`} strokeDashoffset="25" strokeLinecap="round"
                  style={{ transition: 'stroke-dasharray 0.8s ease', filter: `drop-shadow(0 0 4px ${theme.accent}80)` }}/>
              </svg>
            </div>
            <div>
              <p className="text-xs" style={{ color: theme.muted }}>Last session score</p>
              <p className="text-xl font-bold" style={{ color: theme.accent, fontFamily: 'Syne, sans-serif' }}>
                {score}<span className="text-sm font-normal" style={{ color: theme.dim }}> / 100</span>
              </p>
            </div>
            <button onClick={() => navigate('/session')} className="ml-auto glass-btn px-3 py-1.5 text-xs font-medium"
              style={{ color: theme.accent }}>Practice</button>
          </div>
        )}

      </div>
    </div>
  );
}
